import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash, PencilSimple, Scales, ArrowRight, CaretRight, Buildings, Storefront, ArrowsLeftRight, ClipboardText, FileText, WarningCircle } from "@phosphor-icons/react";
import { SemaphoreBadge, SEMAPHORE } from "./SemaphoreBadge";
import ObligationDocsDrawer from "./ObligationDocsDrawer";

const SemaphoreDot = ({ status }) => <span className={`w-2 h-2 rounded-full ${SEMAPHORE[status]?.dot || "bg-neutral-300"}`}/>;

const FILTERS = [
  { k: "all", label: "Todas" },
  { k: "sin_documentos", label: "Sin documentos" },
  { k: "vencido", label: "Vencidas" },
  { k: "proximo", label: "Por vencer" },
  { k: "vigente", label: "Vigentes" },
];

const plural = (n, s, p) => `${n} ${n === 1 ? s : p}`;

const ObligationTable = ({ rows, installations, actions }) => {
  const navigate = useNavigate();
  return (
    <table className="w-full text-sm table-fixed">
      <thead>
        <tr className="border-b border-neutral-200 text-left text-[11px] uppercase tracking-widest text-neutral-500 bg-neutral-50/40">
          <th className="px-5 py-2 font-semibold w-[31%]">Obligación</th>
          <th className="px-3 py-2 font-semibold w-[11%]">Categoría</th>
          <th className="px-3 py-2 font-semibold w-[10%]">Frecuencia</th>
          <th className="px-3 py-2 font-semibold w-[14%]">Instalación</th>
          <th className="px-3 py-2 font-semibold text-center w-[9%]">Documentos</th>
          <th className="px-3 py-2 font-semibold w-[14%]">Estado</th>
          <th className="px-5 py-2 font-semibold text-right w-[11%]">Acciones</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(o => (
          <tr key={o.id} className={`border-b border-neutral-100 last:border-0 hover:bg-blue-50/40 cursor-pointer transition-colors group ${o.doc_status === "sin_documentos" ? "bg-amber-50/50 shadow-[inset_3px_0_0_#f59e0b]" : ""}`} data-testid={`obligation-${o.id}`} data-doc-status={o.doc_status}
            onClick={(e) => { if (e.target.closest("button,a")) return; navigate(`/obligaciones/${o.id}`); }}>
            <td className="px-5 py-2.5">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 bg-emerald-50 text-emerald-700 rounded-md flex items-center justify-center border border-emerald-100"><Scales size={13} weight="duotone"/></div>
                <div>
                  <Link to={`/obligaciones/${o.id}`} className="font-semibold hover:text-blue-700 inline-flex items-center gap-1" data-testid={`obl-link-${o.id}`}>
                    {o.name} <ArrowRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity"/>
                  </Link>
                  {o.responsible && <div className="text-xs text-neutral-500">{o.responsible}</div>}
                  {o.doc_status === "sin_documentos" && <div className="text-[11px] font-semibold text-amber-700 inline-flex items-center gap-1" data-testid={`obl-nodocs-alert-${o.id}`}><WarningCircle size={12} weight="fill"/> Requiere documento</div>}
                </div>
              </div>
            </td>
            <td className="px-3 py-2.5 text-neutral-600">{o.category || "—"}</td>
            <td className="px-3 py-2.5"><span className="text-xs font-semibold text-amber-700">{o.frequency}</span></td>
            <td className="px-3 py-2.5 text-neutral-600">{installations.find(i => i.id === o.installation_id)?.name || "—"}</td>
            <td className="px-3 py-2.5 text-center">
              <button type="button" onClick={() => actions.onOpenDocs(o)}
                title={`Ver ${o.documents_count || 0} documento(s)`} data-testid={`obl-docs-count-${o.id}`}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold tabular-nums transition-colors ${o.documents_count ? "bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-600 hover:text-white" : "bg-neutral-100 text-neutral-400 hover:bg-neutral-200"}`}>
                <FileText size={12} weight="duotone"/>{o.documents_count || 0}
              </button>
            </td>
            <td className="px-3 py-2.5"><SemaphoreBadge status={o.doc_status} days={o.days_to_expire} testid={`obl-semaphore-${o.id}`}/></td>
            <td className="px-5 py-2.5 text-right whitespace-nowrap">
              {actions.canEdit && <button onClick={() => actions.onEdit(o)} className="text-blue-600 mr-3" data-testid={`edit-obligation-${o.id}`}><PencilSimple size={16}/></button>}
              {actions.isAdmin && <button onClick={() => actions.onReassign(o.id)} title="Reasignar documentos" className="text-neutral-500 hover:text-blue-600 mr-3" data-testid={`reassign-obligation-${o.id}`}><ArrowsLeftRight size={16}/></button>}
              {actions.canDelete && <button onClick={() => actions.onDelete(o.id)} className="text-rose-500" data-testid={`delete-obligation-${o.id}`}><Trash size={16}/></button>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const AuthorityGroup = ({ cid, auth, rows, open, onToggle, ...rest }) => (
  <div className="border border-neutral-200 rounded-md overflow-hidden bg-white" data-testid={`auth-panel-${cid}-${auth}`}>
    <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-2.5 bg-neutral-50/70 hover:bg-neutral-100 transition-colors group" data-testid={`auth-panel-toggle-${cid}-${auth}`}>
      <CaretRight size={14} weight="bold" className={`transition-transform duration-200 ${open ? "rotate-90 text-blue-600" : "text-neutral-400"}`}/>
      <Buildings size={15} weight="duotone" className="text-indigo-600"/>
      <span className="font-display font-bold text-sm tracking-tight">{auth}</span>
      <span className="ml-auto text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">{plural(rows.length, "obligación", "obligaciones")}</span>
    </button>
    {open && <div className="border-t border-neutral-200"><ObligationTable rows={rows} {...rest}/></div>}
  </div>
);

const CompanyGroup = ({ company, rows, open, onToggle, authOpen, onToggleAuth, ...rest }) => {
  const byAuth = useMemo(() => {
    const g = {};
    rows.forEach(o => { const a = o.authority || "Sin autoridad"; (g[a] = g[a] || []).push(o); });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);
  const noDocs = rows.filter(o => o.doc_status === "sin_documentos").length;
  return (
    <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden shadow-sm" data-testid={`company-group-${company.id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-5 py-4 hover:bg-neutral-50 transition-colors group" data-testid={`company-group-toggle-${company.id}`}>
        <CaretRight size={16} weight="bold" className={`transition-transform duration-200 ${open ? "rotate-90 text-blue-600" : "text-neutral-400 group-hover:text-blue-600"}`}/>
        <div className="w-9 h-9 bg-blue-50 text-blue-700 rounded-md flex items-center justify-center border border-blue-100"><Storefront size={18} weight="duotone"/></div>
        <div className="text-left min-w-0">
          <div className="font-display font-bold text-base tracking-tight truncate">{company.name}</div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">
            {rows.length ? `${plural(rows.length, "obligación", "obligaciones")} · ${plural(byAuth.length, "autoridad", "autoridades")}` : "Sin obligaciones registradas"}
          </div>
        </div>
        <span className="ml-auto flex items-center gap-2">
          {noDocs > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-semibold" data-testid={`company-nodocs-${company.id}`}>{noDocs} sin documentos</span>}
          <span className="px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-700 text-xs font-bold tabular-nums" data-testid={`company-count-${company.id}`}>{rows.length}</span>
        </span>
      </button>
      {open && (
        <div className="border-t border-neutral-200 bg-neutral-50/40 p-3 space-y-2" data-testid={`company-body-${company.id}`}>
          {rows.length === 0
            ? <p className="text-sm text-neutral-400 italic px-2 py-3" data-testid={`company-empty-${company.id}`}>Sin obligaciones registradas.</p>
            : byAuth.map(([auth, list]) => (
              <AuthorityGroup key={auth} cid={company.id} auth={auth} rows={list}
                open={!!authOpen[`${company.id}|${auth}`]} onToggle={() => onToggleAuth(`${company.id}|${auth}`)} {...rest}/>
            ))}
        </div>
      )}
    </div>
  );
};

// Empresas visibles: las del nodo activo, la empresa activa o todas (super admin global)
function visibleCompanies(user, companies, items) {
  const scids = user?.structure_company_ids;
  const active = user?.active_company_id;
  let list = Array.isArray(scids) ? companies.filter(c => scids.includes(c.id))
    : active ? companies.filter(c => c.id === active) : companies;
  const known = new Set(list.map(c => c.id));
  items.forEach(o => { if (o.company_id && !known.has(o.company_id)) { known.add(o.company_id); list = [...list, { id: o.company_id, name: "Empresa" }]; } });
  return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
}

export const ObligationGroups = ({ user, items: allItems, companies, installations, actions: baseActions, focus }) => {
  const [filter, setFilter] = useState("all");
  const [drawer, setDrawer] = useState(null);
  const actions = { ...baseActions, onOpenDocs: setDrawer };
  const counts = useMemo(() => {
    const c = { all: allItems.length };
    allItems.forEach(o => { c[o.doc_status] = (c[o.doc_status] || 0) + 1; });
    return c;
  }, [allItems]);
  const items = useMemo(() => filter === "all" ? allItems : allItems.filter(o => o.doc_status === filter), [allItems, filter]);
  const list = useMemo(() => {
    const l = visibleCompanies(user, companies, allItems);
    return filter === "all" ? l : l.filter(c => items.some(o => o.company_id === c.id));
  }, [user, companies, allItems, items, filter]);
  const byCompany = useMemo(() => {
    const g = {}; items.forEach(o => { (g[o.company_id] = g[o.company_id] || []).push(o); }); return g;
  }, [items]);
  const [openC, setOpenC] = useState({});
  const [authOpen, setAuthOpen] = useState({});
  const isOpen = (cid) => !!openC[cid];
  const expandFor = (rows) => {
    setOpenC(Object.fromEntries(rows.map(o => [o.company_id, true])));
    setAuthOpen(Object.fromEntries(rows.map(o => [`${o.company_id}|${o.authority || "Sin autoridad"}`, true])));
  };
  const pickFilter = (k) => {
    setFilter(k);
    if (k === "all") { setOpenC({}); setAuthOpen({}); }
    else expandFor(allItems.filter(o => o.doc_status === k));
  };
  // Tras crear/editar: abrir su empresa y autoridad y resaltar la fila
  useEffect(() => {
    if (!focus?.id || !items.some(o => o.id === focus.id)) return;
    setOpenC(p => ({ ...p, [focus.company_id]: true }));
    setAuthOpen(p => ({ ...p, [`${focus.company_id}|${focus.authority || "Sin autoridad"}`]: true }));
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="obligation-${focus.id}"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("row-flash"); setTimeout(() => el.classList.remove("row-flash"), 2200); }
    }, 150);
  }, [focus, items]);
  const setAll = (v) => setOpenC(Object.fromEntries(list.map(c => [c.id, v])));

  const filterBar = (
    <div className="flex flex-wrap gap-2" data-testid="obligations-status-filter">
      {FILTERS.map(f => {
        const on = filter === f.k;
        return (
          <button key={f.k} onClick={() => pickFilter(f.k)} data-testid={`status-filter-${f.k}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-400"}`}>
            {f.k !== "all" && !on && <SemaphoreDot status={f.k}/>}
            {f.label}
            <span className={`tabular-nums ${on ? "text-white/80" : "text-neutral-400"}`}>{counts[f.k] || 0}</span>
          </button>
        );
      })}
    </div>
  );
  const drawerEl = drawer && <ObligationDocsDrawer obligation={drawer} onClose={() => setDrawer(null)}/>;

  if (list.length === 0) {
    return (
      <div className="space-y-3">
        {allItems.length > 0 && filterBar}
        <div className="bg-white border border-neutral-200 rounded-md py-16 text-center text-neutral-400" data-testid="obligations-empty">
          <ClipboardText size={42} className="mx-auto mb-2 text-neutral-300" weight="duotone"/>
          <p>{filter === "all" ? "Sin obligaciones registradas." : "Ninguna obligación coincide con este filtro."}</p>
        </div>
        {drawerEl}
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="obligations-groups">
      {filterBar}
      <div className="flex items-center justify-between text-xs text-neutral-500 px-1">
        <span data-testid="obligations-summary">{plural(list.length, "empresa", "empresas")} · {plural(items.length, "obligación", "obligaciones")}</span>
        <div className="flex gap-3">
          <button onClick={() => { setAll(true); setAuthOpen(Object.fromEntries(items.map(o => [`${o.company_id}|${o.authority || "Sin autoridad"}`, true]))); }} className="hover:text-blue-600 font-semibold" data-testid="expand-all-btn">Expandir todo</button>
          <span className="text-neutral-300">·</span>
          <button onClick={() => { setAll(false); setAuthOpen({}); }} className="hover:text-blue-600 font-semibold" data-testid="collapse-all-btn">Colapsar todo</button>
        </div>
      </div>
      {list.map(c => (
        <CompanyGroup key={c.id} company={c} rows={byCompany[c.id] || []} open={isOpen(c.id)}
          onToggle={() => setOpenC(p => ({ ...p, [c.id]: !isOpen(c.id) }))}
          authOpen={authOpen} onToggleAuth={(k) => setAuthOpen(p => ({ ...p, [k]: !p[k] }))}
          installations={installations} actions={actions}/>
      ))}
      {drawerEl}
    </div>
  );
};

export default ObligationGroups;
